import { useState } from 'react'
import { Plus, Edit2, ToggleLeft, ToggleRight, User } from 'lucide-react'

function DriverModal({ driver, branchId, branchOptions = [], onSave, onClose }) {
  const [fullName, setFullName] = useState(driver?.fullName || '')
  const [licencePlate, setLicencePlate] = useState(driver?.licencePlate || '')
  const [phone, setPhone] = useState(driver?.phone || '')
  const [targetBranch, setTargetBranch] = useState(branchId || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // Drivers belong to a station. An admin viewing "all" would otherwise have
  // one silently picked for them, so let them say which.
  const showBranchPicker = !driver && branchOptions.length > 1

  async function handleSubmit(e) {
    e.preventDefault()
    if (!fullName.trim()) { setError('Full name is required.'); return }
    if (!driver && !targetBranch) { setError('Choose which station this driver belongs to.'); return }
    setSaving(true)
    const result = await onSave({ branchId: targetBranch, fullName: fullName.trim(), licencePlate: licencePlate.trim(), phone: phone.trim() })
    setSaving(false)
    if (!result.success) { setError(result.message); return }
    onClose()
  }

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/50" onClick={onClose} />
      <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-[70] bg-white rounded-2xl shadow-2xl p-5 max-w-sm mx-auto max-h-[calc(100dvh-6rem)] overflow-y-auto">
        <h3 className="font-bold text-gray-800 mb-4">{driver ? 'Edit Driver' : 'Add Driver'}</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          {showBranchPicker && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Station *</label>
              <select
                value={targetBranch}
                onChange={(e) => setTargetBranch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
              >
                {branchOptions.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
            <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)}
              placeholder="e.g. Emeka Okonkwo"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Licence Plate</label>
            <input type="text" value={licencePlate} onChange={(e) => setLicencePlate(e.target.value)}
              placeholder="e.g. ABC-123-XY"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. 08012345678"
              className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm" />
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
          <div className="flex space-x-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white font-semibold text-sm disabled:opacity-60">
              {saving ? 'Saving…' : driver ? 'Update' : 'Add'}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

export default function DriversPanel({ currentUser, branches, drivers, sales, onAdd, onUpdate, onToggle }) {
  // admin/superadmin: add + edit + (de)activate across the org
  // manager:          add within own branch (no edit/toggle)
  // staff:            view only
  const canModify = currentUser.role === 'admin' || currentUser.role === 'superadmin'
  const canAdd = canModify || currentUser.role === 'manager'
  const isBranchScoped = currentUser.role === 'manager' || currentUser.role === 'staff'

  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [search, setSearch] = useState('')
  const [branchFilter, setBranchFilter] = useState(currentUser.branchId || 'all')

  const visibleBranches = isBranchScoped
    ? branches.filter((b) => b.id === currentUser.branchId)
    : branches

  // Branch a newly-added driver is attached to
  const addBranchId = branchFilter !== 'all' ? branchFilter : (visibleBranches[0]?.id || '')

  const filtered = drivers.filter((d) => {
    if (branchFilter !== 'all' && d.branchId !== branchFilter) return false
    if (!search) return true
    const q = search.toLowerCase()
    return d.fullName.toLowerCase().includes(q) || (d.licencePlate && d.licencePlate.toLowerCase().includes(q))
  })

  function getRecentCompanies(driverId) {
    return [...new Set(
      sales
        .filter((s) => s.driverId === driverId && s.companyName)
        .sort((a, b) => new Date(b.saleTime) - new Date(a.saleTime))
        .map((s) => s.companyName)
    )].slice(0, 3)
  }

  async function handleSave(data) {
    if (editing) return onUpdate(editing.id, data)
    return onAdd(data)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-gray-800">Drivers</h2>
          <p className="text-xs text-gray-400">{filtered.length} driver{filtered.length !== 1 ? 's' : ''}</p>
        </div>
        {canAdd && (
          <button
            onClick={() => { setEditing(null); setShowModal(true) }}
            disabled={!addBranchId}
            className="flex items-center space-x-1.5 bg-blue-600 text-white text-sm font-semibold px-3 py-2 rounded-xl disabled:opacity-50"
          >
            <Plus className="w-4 h-4" /><span>Add</span>
          </button>
        )}
      </div>

      <div className="flex space-x-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or plate…"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
        />
        {!isBranchScoped && (
          <select
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-xl text-sm bg-white focus:ring-2 focus:ring-blue-500 outline-none"
          >
            <option value="all">All branches</option>
            {visibleBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center">
          <User className="w-10 h-10 text-gray-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No drivers found.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((d) => {
            const recentCompanies = getRecentCompanies(d.id)
            return (
              <div key={d.id} className={`bg-white rounded-xl border px-4 py-3 ${d.active ? 'border-gray-100' : 'border-gray-100 opacity-60'}`}>
                <div className="flex items-start justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm text-gray-800">{d.fullName}</p>
                    <p className="text-xs text-gray-400">
                      {d.licencePlate || 'No plate'}{d.phone ? ` · ${d.phone}` : ''}
                      {!isBranchScoped && branches.find((b) => b.id === d.branchId) ? ` · ${branches.find((b) => b.id === d.branchId).name}` : ''}
                    </p>
                    {recentCompanies.length > 0 && (
                      <p className="text-xs text-gray-400 mt-0.5 truncate">
                        via {recentCompanies.join(', ')}
                      </p>
                    )}
                  </div>
                  {canModify && (
                    <div className="flex items-center space-x-2 ml-3 flex-shrink-0">
                      <button
                        onClick={() => { setEditing(d); setShowModal(true) }}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => onToggle(d.id, !d.active)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg transition"
                      >
                        {d.active
                          ? <ToggleRight className="w-5 h-5 text-green-500" />
                          : <ToggleLeft className="w-5 h-5 text-gray-300" />
                        }
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {showModal && (
        <DriverModal
          driver={editing}
          branchId={editing ? editing.branchId : addBranchId}
          branchOptions={visibleBranches}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditing(null) }}
        />
      )}
    </div>
  )
}
